Ext.define('SA.data.RelatedDataTableView', {
    alias: 'view.relatedDataTable',
    extend: 'SA.data.ReportView',

    mixins: {
        locations: 'SA.data.mixin.ManyLocations'
    },

    statics: {
        create({project, censusRelease, name, locationItems, attributes}) {
            var attribute = attributes.filter(id => this.supportsAttribute(id))[0] ?? null,
                data = {
                    type: 'relatedDataTable',
                    attribute,
                    attributeTypeFilter: SA.data.AttributesCache.get(attribute)?.type,
                    buildingBlocksCensusRelease: censusRelease,
                    filterVisibility: 'strikeout',
                    locationItems,
                    name
                };

            return new SA.data.RelatedDataTableView({project, data});
        },

        supportsAttribute(attribute) {
            return SA.data.AttributesCache.isPolygonAttribute(attribute);
        }
    },

    constructor() {
        this.callParent(arguments);

        this.getSort();
    },

    addAttributeImpl(attribute) {
        var data = this.data,
            Attributes = SA.data.AttributesCache,
            attributes = Attributes.get([data.attribute, attribute]),
            oldAttribute = attributes[0],
            newAttribute = attributes[1];

        data.attribute = attribute;

        onCached().then(() => {
            var type = Attributes.get(attribute).type,
                hasTypeFilter = data.attributeTypeFilter !== null,
                noLongerSelected = data.attribute !== attribute;

            if (noLongerSelected) {
                return;
            }

            if (!oldAttribute || !newAttribute
                || oldAttribute.group !== newAttribute.group
                || oldAttribute.year !== newAttribute.year
            ) {
                this.groupQuery = null;
                this.relatedAttributes = null;
                this.clearDataMap();
            }

            this.project.addAttribute(attribute);

            if (hasTypeFilter) {
                this.setAttributeTypeFilter(type);
            }

            this.fireEvent('change:attribute', this);
        });

        return false;

        function onCached() {
            return new Promise(resolve => {
                maybeResolve();

                function maybeResolve() {
                    if (Attributes.get(attribute, maybeResolve)) {
                        resolve();
                    }
                }
            });
        }
    },

    addLocationImpl(location) {
        this.mixins.locations.addLocationImpl.call(this, location);

        this.project.addLocation(location);
    },

    createController() {
        return new SA.RelatedDataTableController(this);
    },

    createDataMap() {
        return new SA.data.DataMap({
            country: this.getCountry()
        });
    },

    getAllRelatedAttributes(fn, scope) {
        var attributeId = this.getAttribute(),
            attribute = SA.data.AttributesCache.get(attributeId),
            query = this.groupQuery;

        if (!attribute) {
            return [];
        }

        if (this.relatedAttributes) {
            return this.relatedAttributes;
        }

        if (attributeId !== this.getAttribute()) {
            return;
        }

        if (!query) {
            query = this.groupQuery = SA.data.Query.from({
                fields: SA.data.AttributesCache.getFields(),
                limit: 1000,
                sort: [['asc', 'order']],
                type: 'attributes',

                where: [
                    'and',
                    ['=', 'dataset_series', attribute.dataset_series],
                    ['=', 'group', attribute.group],
                    ['=', 'status', 'visible'],
                    ['=', 'year', attribute.year]
                ]
            });

            query.addSuccess(response => {
                if (this.groupQuery === query) {
                    this.relatedAttributes = response.results;

                    SA.data.AttributesCache.purgeUnreferenced();
                    SA.data.AttributesCache.add(Ext.clone(this.relatedAttributes));
                }
            });

            query.run();
        }

        if (fn) {
            query.addSuccess(fn, scope);
        }

        return [];
    },

    getAllowedAttributeFilterTypes(fn, scope) {
        var attributes = this.getAllRelatedAttributes(fn, scope),
            types = {};

        if (!attributes.length) {
            return [];
        }

        attributes.forEach(attribute => {
            types[attribute.type] = true;
        });

        return Object.keys(types);
    },

    getAttribute() {
        return this.data.attribute;
    },

    getAttributeTypeFilter() {
        return this.data.attributeTypeFilter;
    },

    getAttributes() {
        var attribute = this.getAttribute();

        return attribute ? [attribute] : [];
    },

    getCachedRelatedAttributes() {
        return Ext.Array.from(this.relatedAttributes).map(attribute => attribute.attribute);
    },

    getExportQuery() {
        var attributes = this.getRelatedAttributes(),
            baseColumns = ['name'],
            locations = this.getQueryLocations(),
            query = this.getDataMap().createQueryConfig(SA.Exports.getColumnExpressions({attributes, baseColumns}), locations),
            pluck = Ext.Array.pluck;

        query.sort = [['desc', 'locationSeries'].concat(pluck(locations, 'id'))];

        return query;
    },

    getReferencedAttributes() {
        return SA.Array.unique([this.callParent(), this.getCachedRelatedAttributes()]);
    },

    getRelatedAttributes(fn, scope) {
        this.syncAttributeTypeFilter();

        var all = this.getAllRelatedAttributes(fn, scope),
            typeFilter = this.getAttributeTypeFilter();

        return Ext.Array.clean(all.map(attribute => {
            if (!typeFilter || attribute.type === typeFilter) {
                return attribute.attribute;
            }
        }));
    },

    getReportData(cfg) {
        cfg = cfg || {};

        var map = this.getDataMap();

        map.ensure({
            attributes: this.getRelatedAttributes(cfg.fn, cfg.scope),
            locations: this.getQueryLocations(),

            fn: cfg.fn,
            scope: cfg.scope
        });

        return map.getData();
    },

    getSort() {
        var data = this.data;

        if (!data.sort) {
            this.resetSort();
        }

        return [data.sort[0], SA.data.LocationsCache.toLocationSeries(data.sort[1])];
    },

    getSortDirection() {
        return this.getSort()[0];
    },

    getSortLocation() {
        return this.getSort()[1];
    },

    onCustomLocationUpdateImpl(location) {
        this.getDataMap().removeLocation(SA.data.LocationsCache.normalizeLocation(location));
    },

    removeAttributeImpl(attribute) {
        if (attribute === this.data.attribute) {
            this.data.attribute = null;
        }

        this.callParent([attribute]);
    },

    removeLocationImpl(location) {
        this.callParent([location]);

        if (SA.data.LocationsCache.toLocationSeries(location) === this.getSortLocation()) {
            this.resetSort();
        }
    },

    resetSort() {
        this.setSort(this.getLocation());
    },

    setAttributeTypeFilter(typeFilter) {
        this.setWithEvent('attributeTypeFilter', typeFilter);
    },

    setSort(location, direction) {
        var oldSort = this.data.sort,
            toLocationSeries = SA.data.LocationsCache.toLocationSeries,
            sort;

        if (!location) {
            location = null;
        }
        else if (Array.isArray(location)) {
            direction = location[0];
            location = location[1];
        }



                if (!direction) {
            if (oldSort && toLocationSeries(oldSort[1]) === toLocationSeries(location)) {
                return;
            }

            direction = location ? 'desc' : 'asc';
        }

        sort = [direction, SA.data.LocationsCache.toLocationItem(location)];

        if (SA.Json.differ(sort, oldSort)) {
            this.setWithEvent('sort', sort);
        }
    },

    syncAttributeTypeFilter() {
        var type = this.getAttributeTypeFilter(),
            allowed;

        if (!type) {
            return;
        }

        allowed = this.getAllowedAttributeFilterTypes(this.syncAttributeTypeFilter, this);

        if (!allowed.length || allowed.includes(type)) {
            return;
        }

        type = allowed.includes('count') ? 'count' : null;

        this.setAttributeTypeFilter(type);
    }
});